// useTreeData.js
import { useState, useCallback, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { buildRelationships } from '../utils/treeRules';

const STORAGE_KEY = 'familytree_data';

function loadFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return { persons: [], relationships: [], customColors: {} };
}

function saveToStorage(data) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {}
}

export function useTreeData() {
  const [data, setData] = useState(() => loadFromStorage());
  const dataRef = useRef(data);

  const persist = useCallback((newData) => {
    dataRef.current = newData;
    setData(newData);
    saveToStorage(newData);
  }, []);

  // ── Neue Person hinzufügen ─────────────────────────────────────────────
  const addPerson = useCallback((personData, relationshipsInput) => {
    const rels = Array.isArray(relationshipsInput) ? relationshipsInput : (relationshipsInput ? [relationshipsInput] : []);
    const newPerson = {
      id: uuidv4(),
      name:       personData.name       || '',
      maidenName: personData.maidenName || '',
      birthDate:  personData.birthDate  || '',
      gender:     personData.gender     || 'unknown',
      photo:      personData.photo      || null,
      photoEdit:  personData.photoEdit  || null,
      photoRetouch: personData.photoRetouch || null,
      firstName:  personData.firstName  ?? undefined,
      lastName:   personData.lastName   ?? undefined,
    };

    const currentData = dataRef.current;
    const newRels = buildRelationships(newPerson.id, rels, currentData.relationships);

    persist({
      ...currentData,
      persons:       [...currentData.persons, newPerson],
      relationships: [...currentData.relationships, ...newRels],
    });
    return newPerson.id;
  }, [persist]);

  // ── Person bearbeiten (Stammdaten + Beziehungen) ───────────────────────
  const updatePerson = useCallback((id, personData, relationshipsInput) => {
    const updatedPersons = data.persons.map(p =>
      p.id === id ? { ...p, ...personData } : p
    );

    let updatedRels = data.relationships;

    if (Array.isArray(relationshipsInput)) {
      // Alle bestehenden Beziehungen DIESER Person löschen
      updatedRels = updatedRels.filter(r => r.from !== id && r.to !== id);
      // Neue Beziehungen erstellen
      const newRels = buildRelationships(id, relationshipsInput, updatedRels);
      updatedRels = [...updatedRels, ...newRels];
    }

    persist({ persons: updatedPersons, relationships: updatedRels });
  }, [data, persist]);

  // ── Person löschen ─────────────────────────────────────────────────────
  const deletePerson = useCallback((id) => {
    persist({
      persons:       data.persons.filter(p => p.id !== id),
      relationships: data.relationships.filter(r => r.from !== id && r.to !== id),
    });
  }, [data, persist]);

  const importData = useCallback((d) => {
    persist(d);
    saveToStorage(d);
  }, [persist]);
  const clearAll = useCallback(() => persist({ persons: [], relationships: [], customColors: {} }), [persist]);

  const setCustomColor = useCallback((personId, colorIndex, gen1Siblings) => {
    const prev = data.customColors || {};
    const newCustomColors = { ...prev };

    // Finde die aktuelle Farbe jeder Gen1-Person (custom oder Standard)
    const getEffectiveColor = (id) => {
      if (newCustomColors[id] !== undefined) return newCustomColors[id];
      const idx = gen1Siblings ? gen1Siblings.indexOf(id) : -1;
      return idx >= 0 ? idx % 9 : -1;
    };

    // Prüfe ob diese Farbe bereits von jemand anderem belegt ist
    if (gen1Siblings) {
      const otherHolder = gen1Siblings.find(id => id !== personId && getEffectiveColor(id) === colorIndex);
      if (otherHolder) {
        // Tausche: otherHolder bekommt die bisherige Farbe von personId
        const myOldColor = getEffectiveColor(personId);
        newCustomColors[otherHolder] = myOldColor;
      }
    }

    newCustomColors[personId] = colorIndex;
    persist({ ...data, customColors: newCustomColors });
  }, [data, persist]);

  return {
    persons:       data.persons,
    relationships: data.relationships,
    customColors:  data.customColors || {},
    addPerson,
    updatePerson,
    deletePerson,
    importData,
    clearAll,
    setCustomColor,
  };
}
